import { inject } from '@angular/core';
import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom } from '@blaze-runtime/engine-domain';
import {
  getActiveBinding,
  type PropertiesConfigInputListFiltersModel,
} from '@blaze-shared/elements-domain';
import { type BaseSchema, type BlazeSchema } from '@blaze-shared/schema-domain';

import {
  type ContainerFieldFilter,
  type DefinitionMapper,
  resolveFilterType,
  toActionsDisplayModeStyle,
  toBulkActionsDisplayModeStyle,
  toRecordClickIntent,
} from '../../../ai';
import { type CanvasElementDefinition } from '../../../canvas-elements/element-definition';
import { SearchAndFiltersWrapperService } from '../../../services/search-and-filters-wrapper.service';

import { toPageSize, toTableWidthStyle } from './table.appearance';
import { type TableDefinition } from './table.definition';

export class TableDefinitionMapper implements DefinitionMapper<'table'> {
  private readonly searchAndFiltersWrapperService = inject(SearchAndFiltersWrapperService);

  readonly type = 'table';

  extract(
    element: CanvasElementFrom<'table'>,
    schemas: Readonly<Record<string, BlazeSchema>>,
    definition: CanvasElementDefinition<'table'>,
  ): PartialDeep<TableDefinition> {
    const binding = getActiveBinding(element.props.data, 'value');
    let tableSchema: BaseSchema | undefined;

    if (binding?.dataSource?.id) {
      tableSchema = schemas[binding.dataSource.id];
    }

    const content = element.props.content;
    const search = content.searchAndFilters;
    const defaultsTable = definition.defaults?.content.table;

    const showColumnHeaders = content.table?.showColumnHeaders ?? defaultsTable?.showColumnHeaders;
    const actionsDisplayMode =
      content.table?.actionsDisplayMode ?? defaultsTable?.actionsDisplayMode;
    const bulkActionsDisplayMode =
      content.table?.bulkActionsDisplayMode ?? defaultsTable?.bulkActionsDisplayMode;

    const bulkActions = content.unifiedBulkActions?.map((action) => ({
      $type: action.type,
      name: action.label,
      iconName: action.icon,
      aiDescription: action.aiDescription,
    }));

    const rowActions = content.unifiedRowActions?.map((action) => ({
      $type: action.type,
      name: action.label,
      iconName: action.icon,
      aiDescription: action.aiDescription,
    }));

    const fieldFilters = search?.filters
      ?.map((filter) => this.toFieldFilter(element, filter, tableSchema))
      .filter((filter): filter is ContainerFieldFilter => !!filter);

    const table: PartialDeep<TableDefinition> = {
      title: content.title?.label,
      showTitle: content.title?.enabled,
      iconName: content.title?.icon,
      search: search?.searchEnabled,
      showColumnHeaders,
      pageSize: toPageSize(Boolean(content.table?.usePagination), content.pagination?.count),
    };

    if (bulkActions?.length) {
      table.bulkActions = bulkActions;
    }

    if (rowActions?.length) {
      table.rowActions = rowActions;
    }

    if (fieldFilters?.length) {
      table.fieldFilters = fieldFilters;
    }

    if (content.unifiedRowActions?.length && actionsDisplayMode) {
      table.rowActionsDisplayMode = toActionsDisplayModeStyle(actionsDisplayMode);
    }

    if (content.unifiedBulkActions?.length && bulkActionsDisplayMode) {
      table.bulkActionsDisplayMode = toBulkActionsDisplayModeStyle(bulkActionsDisplayMode);
    }

    if (content.rowClick?.enabled) {
      table.rowClick = toRecordClickIntent(content.rowClick.action);
    }

    if (content.itemAdd?.enabled) {
      table.addNewRow = { label: content.itemAdd.label };
    }

    const width = toTableWidthStyle(element.width);
    if (width) {
      table.width = width;
    }

    return table;
  }

  private toFieldFilter(
    element: CanvasElementFrom<'table'>,
    filter: PropertiesConfigInputListFiltersModel,
    tableSchema: BaseSchema | undefined,
  ): ContainerFieldFilter | undefined {
    const field = tableSchema?.columns.find((column) => column.id === filter.filterField)?.name;
    if (!field) {
      return undefined;
    }

    return {
      label: filter.placeholder ?? '',
      field,
      filterType: resolveFilterType({
        searchAndFiltersWrapperService: this.searchAndFiltersWrapperService,
        containerElementId: element.id,
        filter,
      }),
    };
  }
}
